import { createApp,h,reactive } from 'vue'
import { version } from './package.json'



/*tree shaking imports */
/*https://vueform.com/docs/tree-shaking*/

import Vueform, {
    // Essential form components
    FormErrors,
    FormMessages,

    // Essential element components
    ElementLayout,
    ElementLayoutInline,
    ElementLoader,
    ElementLabelFloating,
    ElementLabel,
    ElementInfo,
    ElementDescription,
    ElementError,
    ElementMessage,
    ElementText,
    ElementAddon,
    ElementAddonOptions,
    ElementRequired,

    // Optional form components
    FormLanguages,
    FormLanguage,
    FormTabs,
    FormTab,
    FormSteps,
    FormStepsControls,
    FormStepsControl,
    FormStep,

    // Elements
    ButtonElement,
    CaptchaElement,
    CheckboxElement,
    CheckboxgroupElement,
    DateElement,
    DatesElement,
    FileElement,
    GroupElement,
    HiddenElement,
    ListElement,
    LocationElement,
    MultifileElement,
    MultiselectElement,
    ObjectElement,
    PhoneElement,
    RadioElement,
    RadiogroupElement,
    SelectElement,
    SignatureElement,
    SliderElement,
    StaticElement,
    TagsElement,
    TextareaElement,
    TextElement,
    ToggleElement,
    EditorElement,
    TTextareaElement,
    TTextElement,
    TEditorElement,

    // Element partials & wrappers
    DragAndDrop,
    CheckboxgroupCheckbox,
    RadiogroupRadio,
    FilePreview,
    DatepickerWrapper,
    EditorWrapper,
} from '@vueform/vueform/src/core.js'

const customComponents = {
    // Essential form components
    FormErrors,
    FormMessages,

    // Essential element components
    ElementLayout,
    ElementLayoutInline,
    ElementLoader,
    ElementLabelFloating,
    ElementLabel,
    ElementInfo,
    ElementDescription,
    ElementError,
    ElementMessage,
    ElementText,
    ElementAddon,
    ElementAddonOptions,
    ElementRequired,

    // Optional form components
    FormLanguages,
    FormLanguage,
    FormTabs,
    FormTab,
    FormSteps,
    FormStepsControls,
    FormStepsControl,
    FormStep,

    // Elements
    ButtonElement,
    CaptchaElement,
    CheckboxElement,
    CheckboxgroupElement,
    // DateElement,
    // DatesElement,
    FileElement,
    GroupElement,
    HiddenElement,
    ListElement,
    // LocationElement,
    MultifileElement,
    MultiselectElement,
    // ObjectElement,
    PhoneElement,
    RadioElement,
    RadiogroupElement,
    SelectElement,
    // SignatureElement,
    // SliderElement,
    StaticElement,
    TagsElement,
    TextareaElement,
    TextElement,
    ToggleElement,
    // EditorElement,
    // TTextareaElement,
    // TTextElement,
    // TEditorElement,

    // Element partials & wrappers
    DragAndDrop,
    CheckboxgroupCheckbox,
    RadiogroupRadio,
    FilePreview,
    // DatepickerWrapper,
    // EditorWrapper,
};


/*end tree shaking imports */

//import theme css
import './theme/customVueformTheme.scss'
import './theme/vueFormPlugin.scss'


import vueformConfig from './vueform.config'
import DynamicForm from './DynamicForm.vue'
import Cookie from 'js-cookie';
import propReader from './utils/dataprop-reader.js'


const pageForms = [];

//NOTES:
//this loader supports script datablocks on a page with the class name ads-vueform-data
//It will then attempt to convert that block into a functional VueForm form
//Requirements: 
//1. Your model json used in the script block should be wrapped with a group container named "unique_container"
//  It will function without it, but it will not make any effort to make ids unique on a page
//2. Your model json can contain a defaults field which will attempt to set defaults in the form using the object provided
//3. Your model must contain at least a schema and a form field
//4. Your model can contain a recaptchaKey field to be used by default by recaptcha elements
//Data props on the script block:
//1. locale - locale string like 'en' or 'fr_CA' that align with the supported locales in the vueform build (current en, and fr_CA
//2. defaults - a json value to be used to set default values in the form
//3. recaptchakey - a public key to be used by recaptcha elements on the page
//4. endpoint - the submission endpoint for the form, defaults to /vueform/process
// Data props take presedence over like-named fields in the json model


function activateForm(scriptElement) {
    //model data content
    try {
        const model = JSON.parse(scriptElement.textContent);

        //set model defaults
        const defaults = propReader.readPropObjOrDefault(scriptElement, 'defaults')
        if (defaults)
            model.defaults = defaults;

        //set recaptcha key
        const recaptchaKey = propReader.readPropStringOrDefault(scriptElement, 'recaptchakey');
        if (recaptchaKey)
            model.recaptchaKey = recaptchaKey;

        //endpoint url
        const endpointUrl = propReader.readPropStringOrDefault(scriptElement, 'endpoint');
        if (endpointUrl)
            model.form.endpoint = endpointUrl;

        //set context data
        const context = propReader.readPropObjOrDefault(scriptElement, 'context');
        if (context) {
            model.context = context;
        }

        //get gtm event definition
        const gtmEvent = propReader.readPropObjOrDefault(scriptElement, 'gtmSuccessEvent');
        if (gtmEvent) {
            model.gtmSuccessEvent = gtmEvent;
        }

        //get thank you url
        const thankYouUrl = propReader.readPropStringOrDefault(scriptElement, 'thankYouUrl');
        if (thankYouUrl) {
            model.thankYouUrl = thankYouUrl;
        }

        //callback fn
        const callBackFn = propReader.readPropStringOrDefault(scriptElement, 'callbackfn');
        if (callBackFn) {
            model.callBackFn = callBackFn;
        }

        //hide placeholder float
        const hideFloatingPlaceholders = propReader.readPropStringOrDefault(scriptElement, 'hideFloatingPlaceholders');
        if (hideFloatingPlaceholders) {
            model.hideFloatingPlaceholders = hideFloatingPlaceholders;
        }

        //get locale
        const locale = propReader.readPropStringOrDefault(scriptElement, 'locale', 'en');

        //create a vue app for DynamicForm and use Vueform and custom components inside it
        const dynamicFormApp = createApp(DynamicForm, {
            model: model,
            locale: locale
        });
        dynamicFormApp.use(Vueform, { ...vueformConfig, components: customComponents });
        
        //create an element to mount to and mount the formInstance to it
        const mountElement = document.createElement("div");
        scriptElement.after(mountElement);
        dynamicFormApp.mount(mountElement);
        scriptElement.remove();

        pageForms.push(dynamicFormApp);
    } catch (e) {
        console.log('Form failed to init', scriptElement, e);
    }
}


function activateAllForms() {
    //search for all ads-vueform-data script blocks on a page and create a vue for each
    const formBlocks = document.querySelectorAll("script.ads-vueform-data");
    for (let i = 0; i < formBlocks.length; i++) {
        const el = formBlocks[i];

        //model data content
        activateForm(el);
    }

}

//process all elements that are available now
activateAllForms();

//provide some globals for use by debugging
window.adsVueForm = {
    version: version,
    activateAllForms,
    activateForm,
    pageForms,
    Cookie,
    setAllLocales: function (locale) {
        console.log(this.pageForms);
        throw "This is not implemented, but would switch all forms on the page to a new locale"
    }
}